<script setup lang="ts">
import { useHead, useRoute, useRouter } from '#app';
import { orderBlogPostsByFeaturedSlugs, useBlog, type BlogListConfig } from '@/composables/useBlog';
import { provideSeoDetailLanguagePaths } from '@/composables/useSeoDetailLanguageNavigation';
import { useSeoLocaleAvailability } from '@/composables/useSeoLocaleAvailability';
import { applyBlogPostsListLocale } from '@/lib/blog-locale';
import {
  absoluteLocalizedUrl,
  buildIndexHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';
import AppNav from '@/components/AppNav.vue';
import AppFooter from '@/components/AppFooter.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import { computed, ref } from 'vue';
import { i18n, t } from '@/i18n';

interface BlogCategory {
  title: string;
}

interface BlogAuthor {
  name: string;
  avatar?: string;
}

interface BlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt?: any[]; // Portable Text array
  image?: string;
  publishedAt?: string;
  categories?: BlogCategory[];
  category?: string;
  tags?: string[];
  readTime?: number;
  author?: BlogAuthor;
}

const router = useRouter();
const route = useRoute();
const siteLocale = siteLocaleFromRouteParam(route.params.locale);
const { fetchBlogListConfig, fetchBlogPosts } = useBlog();
const { fetchAvailableLocales } = useSeoLocaleAvailability();
const blogIndexPath = computed(() => localizedPath(siteLocale, '/blog'));
const blogPagePath = (page: number) => localizedPath(siteLocale, `/blog/page/${page}`);
const blogPostPath = (slug: string) => localizedPath(siteLocale, `/blog/${slug}`);

// Pagination settings
const postsPerPage = 9;
const currentPage = ref(1); // Always page 1 for index

const { data: blogListConfig } = await useAsyncData<BlogListConfig>(
  () => `blog-list-config-${siteLocale}`,
  () => fetchBlogListConfig(siteLocale),
  {
    server: true,
    lazy: false,
    default: () => ({
      language: siteLocale,
      exists: false,
      seoTitle: '',
      seoDescription: '',
      keywords: [],
      title: '',
      subtitle: '',
      featuredArticleSlugs: [],
    }),
  }
);

// Fetch blog posts from Sanity
const { data: allPosts } = await useAsyncData<BlogPost[]>(
  () => `blog-posts-${siteLocale}`,
  async () => {
    try {
      const data = await fetchBlogPosts(siteLocale);
      const localizedPosts = applyBlogPostsListLocale((data || []) as BlogPost[]);
      return orderBlogPostsByFeaturedSlugs(
        localizedPosts,
        blogListConfig.value.featuredArticleSlugs
      );
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      return [];
    }
  },
  {
    server: true,
    lazy: false,
    default: () => [],
  }
);
const { data: availableLocales } = await useAsyncData(
  'blog-index-locale-availability',
  () => fetchAvailableLocales('blog'),
  { server: true, lazy: false, default: () => [] }
);

// Pagination calculations
const totalPosts = computed(() => allPosts.value?.length || 0);
const headLocales = computed(() =>
  availableLocales.value.length ? availableLocales.value : totalPosts.value > 0 ? [siteLocale] : []
);
const languagePaths = computed(() =>
  Object.fromEntries(
    headLocales.value.map((availableLocale) => [
      availableLocale,
      localizedPath(availableLocale, '/blog'),
    ])
  )
);
provideSeoDetailLanguagePaths(languagePaths);
const hasPublishedLocaleContent = computed(
  () => totalPosts.value > 0 && headLocales.value.includes(siteLocale)
);
const totalPages = computed(() => Math.ceil(totalPosts.value / postsPerPage));
const startIndex = computed(() => (currentPage.value - 1) * postsPerPage);
const endIndex = computed(() => startIndex.value + postsPerPage);
const paginatedPosts = computed(() => {
  if (!allPosts.value) return [];
  return allPosts.value.slice(startIndex.value, endIndex.value);
});

// Navigation functions
function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  if (page === 1) {
    router.push(blogIndexPath.value);
  } else {
    router.push(blogPagePath(page));
  }
  // Scroll to top
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
}

// Generate page numbers for pagination buttons
const pageNumbers = computed(() => {
  const pages: (number | string)[] = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    // Show pages around current page
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }

    // Always show last page
    pages.push(total);
  }

  return pages;
});

const pageTitle = computed(
  () => blogListConfig.value.seoTitle || t('landingPages.blog.index.seo.title')
);
const documentTitle = computed(() =>
  blogListConfig.value.seoTitle ? pageTitle.value : `${pageTitle.value} | Veeso`
);
const pageDescription = computed(
  () => blogListConfig.value.seoDescription || t('landingPages.blog.index.seo.description')
);
const pageKeywords = computed(() => blogListConfig.value.keywords.join(', '));
const heroSubtitle = computed(
  () => blogListConfig.value.subtitle || t('landingPages.blog.index.hero.subtitle')
);
const heroTitle = computed(
  () => blogListConfig.value.title || t('landingPages.blog.index.hero.title')
);
const faqSectionTitle = computed(() => t('landingPages.blog.index.faq.title'));

// Canonical URL - each page should have its own canonical URL
const canonicalUrl = computed(() => {
  return absoluteLocalizedUrl(siteLocale, '/blog');
});

// Generate prev/next URLs for pagination
const prevUrl = computed(() => {
  // Page 1 doesn't have previous
  return null;
});

const nextUrl = computed(() => {
  if (totalPages.value > 1) {
    return absoluteLocalizedUrl(siteLocale, '/blog/page/2');
  }
  return null;
});

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => {
  const links = [
    { rel: 'canonical', href: canonicalUrl.value },
    ...(hasPublishedLocaleContent.value
      ? buildIndexHreflangLinks('blog', 'https://veeso.ai', headLocales.value)
      : []),
  ];

  // Add prev/next links for pagination (helps with SEO and prevents orphan pages)
  if (prevUrl.value) {
    links.push({ rel: 'prev', href: prevUrl.value });
  }
  if (nextUrl.value) {
    links.push({ rel: 'next', href: nextUrl.value });
  }

  return {
    title: documentTitle.value,
    htmlAttrs: { lang: siteLocale },
    meta: [
      ...(!hasPublishedLocaleContent.value ? [{ name: 'robots', content: 'noindex,follow' }] : []),
      {
        name: 'description',
        content: pageDescription.value,
      },
      ...(pageKeywords.value
        ? [
            {
              name: 'keywords',
              content: pageKeywords.value,
            },
          ]
        : []),
      {
        property: 'og:title',
        content: pageTitle.value,
      },
      {
        property: 'og:description',
        content: pageDescription.value,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: canonicalUrl.value,
      },
      {
        property: 'og:locale',
        content: ogLocaleFor(siteLocale),
      },
      {
        property: 'og:image',
        content: ogImageUrl.value,
      },
      {
        property: 'og:image:width',
        content: '1200',
      },
      {
        property: 'og:image:height',
        content: '630',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: pageTitle.value,
      },
      {
        name: 'twitter:image',
        content: ogImageUrl.value,
      },
    ],
    link: links,
  };
});

function formatDate(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const dateLocale = {
    en: 'en-US',
    ja: 'ja-JP',
    ko: 'ko-KR',
    es: 'es-ES',
  }[siteLocale];
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

type BlogFaqItem = {
  question: string;
  answer: string;
};

const faqItems = computed(() => {
  const raw = i18n.getRaw('landingPages.blog.index.faq.items');
  return Array.isArray(raw) ? (raw as BlogFaqItem[]) : [];
});

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <AppNav :active-path="blogIndexPath" />

    <!-- Hero Section -->
    <section
      class="relative pt-48 pb-32 overflow-hidden learn-hero"
      :aria-label="t('ui.a11y.heroSection')"
    >
      <div class="relative container mx-auto px-6 md:px-4 text-center z-10">
        <h1
          class="font-boldonse text-5xl md:text-7xl text-white uppercase tracking-tight mb-8 drop-shadow-sm"
        >
          {{ heroTitle }}
        </h1>
        <p class="font-boldonse text-white/90 text-xl max-w-2xl mx-auto uppercase">
          {{ heroSubtitle }}
        </p>
      </div>
    </section>

    <main class="relative z-[1]">
      <!-- Blog Posts Grid -->
      <section
        class="max-w-[1200px] mx-auto px-4 md:px-8 pt-24 pb-24"
        :aria-label="t('ui.a11y.blogPosts')"
      >
        <div
          v-if="paginatedPosts && Array.isArray(paginatedPosts) && paginatedPosts.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <NuxtLink
            v-for="post in paginatedPosts"
            :key="post._id"
            :to="blogPostPath(post.slug)"
            class="flex flex-col bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:bg-black/5 hover:scale-[1.02] shadow-[0_3px_7px_0px_rgba(0,0,0,0.05)]"
          >
            <!-- Featured Image -->
            <div v-if="post?.image" class="h-48 relative overflow-hidden bg-black/5">
              <img
                :src="post.image"
                :alt="post?.title || t('landingPages.blog.index.image_alt_fallback')"
                class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>

            <!-- Content -->
            <div class="flex-1 flex flex-col p-6">
              <!-- Category & Meta -->
              <div class="flex items-center gap-3 mb-3">
                <span
                  v-if="
                    post?.category ||
                    (post?.categories &&
                      Array.isArray(post.categories) &&
                      post.categories.length > 0)
                  "
                  class="text-xs font-medium px-3 py-1 rounded-full bg-black/5 text-black/60"
                >
                  {{ post.category || post.categories?.[0]?.title }}
                </span>
                <span v-if="post?.readTime" class="text-xs text-black/50">
                  {{ t('landingPages.blog.index.min_read', { count: post.readTime }) }}
                </span>
              </div>

              <!-- Title -->
              <h3 class="text-xl md:text-2xl font-normal text-black/90 mb-3 line-clamp-2">
                {{ post?.title }}
              </h3>

              <!-- Excerpt -->
              <p
                v-if="post?.excerpt"
                class="text-base text-black/60 font-normal leading-relaxed mb-4 line-clamp-3"
              >
                {{
                  Array.isArray(post.excerpt)
                    ? post.excerpt
                        .map((block: any) => {
                          if (block._type === 'block' && block.children) {
                            return block.children
                              .map((child: any) => {
                                if (typeof child === 'string') return child;
                                if (child.text) return child.text;
                                return '';
                              })
                              .join('');
                          }
                          return '';
                        })
                        .join(' ')
                        .trim()
                    : post.excerpt
                }}
              </p>

              <!-- Author & Date -->
              <div class="mt-auto pt-4 flex items-center justify-between">
                <div v-if="post?.author" class="flex items-center gap-2">
                  <img
                    v-if="post.author?.avatar"
                    :src="post.author.avatar"
                    :alt="post.author?.name || t('landingPages.blog.index.author_alt_fallback')"
                    class="w-8 h-8 rounded-full"
                  />
                  <span class="text-sm text-black/70">
                    {{ post.author?.name }}
                  </span>
                </div>
                <span v-if="post?.publishedAt" class="text-sm text-black/50">
                  {{ formatDate(post.publishedAt) }}
                </span>
              </div>

              <!-- Read More -->
              <div class="mt-4 pt-4 border-t border-black/5">
                <span class="text-sm font-medium text-black/80 inline-flex items-center gap-2">
                  {{ t('landingPages.blog.index.card.read_more') }}
                  <span class="transition-transform group-hover:translate-x-1">→</span>
                </span>
              </div>
            </div>
          </NuxtLink>
        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-24">
          <p class="text-lg text-black/60">{{ t('landingPages.blog.index.empty_state') }}</p>
        </div>

        <!-- Pagination Controls -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-center gap-2 mt-12"
          :aria-label="t('ui.a11y.pagination')"
        >
          <!-- Previous Button -->
          <button
            @click="prevPage"
            :disabled="currentPage === 1"
            :aria-label="t('landingPages.blog.index.pagination.prev_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.blog.index.pagination.prev') }}
          </button>

          <!-- Page Numbers -->
          <div class="flex items-center gap-1">
            <button
              v-for="(pageNum, index) in pageNumbers"
              :key="`page-${index}`"
              @click="typeof pageNum === 'number' ? goToPage(pageNum) : null"
              :disabled="typeof pageNum === 'string'"
              :aria-label="
                typeof pageNum === 'number'
                  ? t('ui.a11y.goToPage', { page: String(pageNum) })
                  : undefined
              "
              :aria-current="pageNum === currentPage ? 'page' : undefined"
              class="min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 disabled:cursor-default"
              :class="
                pageNum === currentPage
                  ? 'bg-black/90 text-white'
                  : typeof pageNum === 'string'
                    ? 'text-black/30 cursor-default'
                    : 'bg-white text-black/70 hover:bg-black/5'
              "
            >
              {{ pageNum }}
            </button>
          </div>

          <!-- Next Button -->
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            :aria-label="t('landingPages.blog.index.pagination.next_aria')"
            class="px-4 py-2 rounded-lg bg-white text-black/70 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          >
            {{ t('landingPages.blog.index.pagination.next') }}
          </button>
        </div>

        <!-- Pagination Info -->
        <div v-if="totalPosts > 0" class="text-center mt-6 text-sm text-black/50">
          {{
            t('landingPages.blog.index.showing_posts', {
              start: startIndex + 1,
              end: Math.min(endIndex, totalPosts),
              total: totalPosts,
            })
          }}
        </div>
      </section>

      <!-- FAQ Section -->
      <section class="py-24 bg-white">
        <div class="container mx-auto px-6 md:px-4">
          <div class="max-w-[800px] mx-auto">
            <h2
              class="font-boldonse text-[clamp(1.5rem,3vw,2rem)] text-black/90 uppercase leading-[1.6] mb-12 text-center"
            >
              {{ faqSectionTitle }}
            </h2>
            <div class="space-y-4">
              <div
                v-for="(item, index) in faqItems"
                :key="index"
                class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
              >
                <button
                  type="button"
                  @click="toggleFaq(index)"
                  class="flex items-center justify-between w-full text-left font-medium text-xl text-black/90 focus:outline-none"
                >
                  <h3 class="font-medium text-xl text-black/90">{{ item.question }}</h3>
                  <span
                    class="transform transition-transform duration-300 text-2xl"
                    :class="{ 'rotate-45': expandedFaqIndex === index }"
                  >
                    +
                  </span>
                </button>
                <div
                  class="grid transition-all duration-300 ease-in-out overflow-hidden"
                  :class="
                    expandedFaqIndex === index
                      ? 'grid-rows-[1fr] opacity-100 mt-4'
                      : 'grid-rows-[0fr] opacity-0'
                  "
                >
                  <div class="overflow-hidden">
                    <p class="text-black/60 leading-relaxed text-lg">
                      {{ item.answer }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <FeaturesRoles />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
.learn-hero {
  background: linear-gradient(
    to bottom,
    #94b3da 0%,
    #d0e3f6 15%,
    #f5f1e6 30%,
    #e8d5c3 45%,
    #97a4bc 60%,
    #395e94 75%,
    #08306f 90%,
    #02245d 100%
  );
}

.learn-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
  mix-blend-mode: overlay;
  opacity: 0.3;
  z-index: 1;
}
</style>
